<script setup>
import LoadingSpinner from '@js/components/LoadingSpinner.vue';
import ContainerRoot from '@js/components/containers/ContainerRoot.vue';
import DataTableLayout from '@js/components/tables/DataTableLayout.vue';
import NumberCell from '@js/components/tables/cells/NumberCell.vue';
import PercentageCell from '@js/components/tables/cells/PercentageCell.vue';
import PriceCell from '@js/components/tables/cells/PriceCell.vue';
import TextCell from '@js/components/tables/cells/TextCell.vue';
import { useDataTable } from '@js/utils/dataTable';
import { useRoute } from '@js/utils/route';
import { createColumnHelper } from '@tanstack/vue-table';
import { h } from 'vue';

const { route } = useRoute();

const columnHelper = createColumnHelper();

const {
    loading,
    table,
    count,
    globalFilter,
    filters,
    availableFilters,
    setGlobalFilter,
    setFilters,
} = useDataTable({
    columns: [
        columnHelper.accessor(() => null, {
            id: 'name',
            header: () => 'Nom',
            cell: (props) =>
                h(TextCell, {
                    text: props.row.original.name,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'averageCustomerLifeTime',
            header: () => 'Durée de vie du client' + ' 1095',
            cell: (props) =>
                h(NumberCell, {
                    number: props.row.original.averageCustomerLifeTime,
                    withDecimals: true,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'averageNumberOfPurchasePerYears',
            header: () => "Nb moyen d'achats par an",
            cell: (props) =>
                h(NumberCell, {
                    number: props.row.original.averageNumberOfPurchasePerYears,
                    withDecimals: true,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'averageBasket',
            header: () => 'Panier moyen' + ' 365',
            cell: (props) =>
                h(PriceCell, {
                    price: props.row.original.averageBasket,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'cac',
            header: () => 'CAC' + ' 365',
            cell: (props) =>
                h(PriceCell, {
                    price: props.row.original.cac,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'grossMarginToOrderTurnoverRatio',
            header: () => 'Marge brute' + ' 365',
            cell: (props) =>
                h(PercentageCell, {
                    percentage:
                        props.row.original.grossMarginToOrderTurnoverRatio,
                    withDecimals: true,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'grossLtv',
            header: () => 'LTV brute',
            cell: (props) =>
                h(PriceCell, {
                    price: props.row.original.grossLtv,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'netLtv',
            header: () => 'LTV nette',
            cell: (props) =>
                h(PriceCell, {
                    price: props.row.original.netLtv,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'minimalGrossRoas',
            header: () => 'ROAS brut',
            cell: (props) =>
                h(NumberCell, {
                    number: props.row.original.minimalGrossRoas,
                    withDecimals: true,
                    color:
                        props.row.original.minimalGrossRoas >
                        props.row.original.minimalTargetRoas
                            ? 'green'
                            : 'red',
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'minimalNetRoas',
            header: () => 'ROAS net',
            cell: (props) =>
                h(NumberCell, {
                    number: props.row.original.minimalNetRoas,
                    withDecimals: true,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'minimalTargetRoas',
            header: () => 'ROAS cible min',
            cell: (props) =>
                h(NumberCell, {
                    number: props.row.original.minimalTargetRoas,
                    withDecimals: true,
                    color:
                        props.row.original.minimalGrossRoas >
                        props.row.original.minimalTargetRoas
                            ? 'green'
                            : 'red',
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'coefficientGoogleAds',
            header: () => 'Coef Google Ads',
            cell: (props) =>
                h(NumberCell, {
                    number: props.row.original.coefficientGoogleAds,
                    withDecimals: true,
                }),
        }),
    ],
    url: route('front.projectManagement.sem.postLifeTimeValues'),
});
</script>

<template>
    <LoadingSpinner v-if="loading" />

    <template v-else>
        <ContainerRoot class="wysiwyg mb-4">
            <ul>
                <li>
                    La LTV (Lifetime Value) d'un client représente la valeur
                    totale qu’un client génère pour une entreprise sur toute la
                    durée de sa relation avec elle. Son calcul permet
                    d’optimiser les stratégies marketing, d’adapter les budgets
                    d’acquisition et de maximiser la rentabilité.
                </li>

                <li>Calcul réalisé sur les 1095 derniers jours</li>

                <li>
                    Taux de perte annuel = (Total des clients des 730 premiers
                    jours n’ayant pas commandé les 365 derniers jours) / Total
                    des clients des 730 premiers jours X 100
                </li>

                <li>Durée de vie client moyenne = 1 / Taux de perte annuel</li>

                <li>
                    Fréquence annuelle d’achat par client = Nb total de
                    commandes les 365 derniers jours / Nb de clients uniques
                    ayant commandé les 365 derniers jours
                </li>

                <li>Panier moyen = Panier moyen des 365 des derniers jours</li>

                <li>
                    CAC des 365 derniers jours = Dépenses Pub des 365 derniers
                    jours / Nb de clients ayant passé leur première commande les
                    365 derniers jours
                    <ul>
                        <li>
                            Montant moyen investi en publicité pour obtenir la
                            première commande d'un nouveau client
                        </li>
                    </ul>
                </li>

                <li>
                    Marge brute des 365 derniers jours = Marge brute des 365
                    derniers jours en % sur le tableau de bord du client
                </li>

                <li>
                    LTV brute = Panier moyen (365 derniers jours) X Fréquence
                    annuelle d’achat par client (1095 derniers jours) X Durée de
                    vie client moyenne
                    <ul>
                        <li>
                            CA total moyen qu'un client génère sur toute sa
                            durée de vie
                        </li>
                    </ul>
                </li>

                <li>
                    LTV nette = LTV brute X Marge brute moyenne (365 derniers
                    jours)
                    <ul>
                        <li>
                            Bénéfice total moyen qu'un client génère sur toute
                            sa durée de vie
                        </li>
                    </ul>
                </li>

                <li>
                    ROAS brut = LTV brute / CAC
                    <ul>
                        <li>
                            Le CA réel généré pour chaque euro investi en
                            publicité
                        </li>
                    </ul>
                </li>

                <li>
                    ROAS net = LTV nette / CAC
                    <ul>
                        <li>
                            Le bénéfice réel (en tenant compte de la marge
                            brute) généré pour chaque euro investi en publicité
                        </li>
                    </ul>
                </li>

                <li>
                    ROAS cible min = 1 / Marge brute
                    <ul>
                        <li>
                            Le seuil de rentabilité réel : c’est le CA minimum
                            qu’il faut obligatoirement atteindre par euro
                            dépensé en publicité pour être vraiment rentable
                        </li>
                    </ul>
                </li>

                <li>
                    Coef Google Ads = LTV brute / Panier moyen (365 derniers
                    jours)
                </li>
            </ul>
        </ContainerRoot>

        <DataTableLayout
            :table="table"
            :count="count"
            :global-filter="globalFilter"
            :filters="filters"
            :available-filters="availableFilters"
            :set-filters="setFilters"
            :set-global-filter="setGlobalFilter"
            wrap-headers
        />
    </template>
</template>
